import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { auditService, AuditLog, AuditLogsQueryParams } from '../../services/api/audit';
import './AuditLogs.css';

const AuditLogs: React.FC = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [selectedLog, setSelectedLog] = useState<AuditLog | null>(null);
  const [exporting, setExporting] = useState(false);

  const [filters, setFilters] = useState<AuditLogsQueryParams>({
    page: 1,
    pageSize: 20,
  });

  useEffect(() => {
    loadLogs();
  }, [filters]);

  const loadLogs = async () => {
    try {
      setLoading(true);
      const response = await auditService.query(filters);
      setLogs(response.items);
      setTotal(response.total);
      setPage(response.page);
    } catch (error) {
      console.error('Failed to load audit logs:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key: keyof AuditLogsQueryParams, value: any) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
      page: 1, // Reset to first page when filter changes
    }));
  };

  const handlePageChange = (newPage: number) => {
    setFilters((prev) => ({
      ...prev,
      page: newPage,
    }));
  };

  const handleExport = async () => {
    try {
      setExporting(true);
      const blob = await auditService.exportCsv(filters);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `audit-logs-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Failed to export audit logs:', error);
      alert('Failed to export audit logs');
    } finally {
      setExporting(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('vi-VN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };

  const formatJson = (data: any) => {
    if (!data) return 'N/A';
    try {
      return JSON.stringify(data, null, 2);
    } catch {
      return String(data);
    }
  };

  const totalPages = Math.ceil(total / pageSize);

  return (
    <div className="audit-logs">
      <div className="audit-logs-header">
        <div>
          <h1>Audit Logs</h1>
          <p>View system activity and changes</p>
        </div>
        <button
          className="btn-export"
          onClick={handleExport}
          disabled={exporting || total === 0}
        >
          {exporting ? 'Exporting...' : 'Export CSV'}
        </button>
      </div>

      <div className="audit-logs-filters">
        <div className="filter-group">
          <label>Action</label>
          <input
            type="text"
            placeholder="Filter by action..."
            value={filters.action || ''}
            onChange={(e) => handleFilterChange('action', e.target.value || undefined)}
          />
        </div>

        <div className="filter-group">
          <label>Target Type</label>
          <input
            type="text"
            placeholder="Filter by target type..."
            value={filters.targetType || ''}
            onChange={(e) => handleFilterChange('targetType', e.target.value || undefined)}
          />
        </div>

        <div className="filter-group">
          <label>From Date</label>
          <input
            type="datetime-local"
            value={filters.fromDate ? filters.fromDate.substring(0, 16) : ''}
            onChange={(e) => handleFilterChange('fromDate', e.target.value ? `${e.target.value}:00` : undefined)}
          />
        </div>

        <div className="filter-group">
          <label>To Date</label>
          <input
            type="datetime-local"
            value={filters.toDate ? filters.toDate.substring(0, 16) : ''}
            onChange={(e) => handleFilterChange('toDate', e.target.value ? `${e.target.value}:00` : undefined)}
          />
        </div>

        <button
          className="btn-reset"
          onClick={() => setFilters({ page: 1, pageSize: 20 })}
        >
          Reset Filters
        </button>
      </div>

      {loading ? (
        <div className="loading">Loading audit logs...</div>
      ) : logs.length === 0 ? (
        <div className="no-data">No audit logs found</div>
      ) : (
        <>
          <div className="audit-logs-table-container">
            <table className="audit-logs-table">
              <thead>
                <tr>
                  <th>Date/Time</th>
                  <th>Actor</th>
                  <th>Action</th>
                  <th>Target Type</th>
                  <th>Target ID</th>
                  <th>IP Address</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {logs.map((log) => (
                  <tr key={log.id}>
                    <td>{formatDate(log.createdAt)}</td>
                    <td>
                      {log.actor ? (
                        <div>
                          <div>{log.actor.email}</div>
                          <div className="text-muted">{log.actor.role}</div>
                        </div>
                      ) : (
                        `User ID: ${log.actorUserId}`
                      )}
                    </td>
                    <td>
                      <span className="action-badge">{log.action}</span>
                    </td>
                    <td>{log.targetType}</td>
                    <td>{log.targetId || '-'}</td>
                    <td>{log.ipAddress || '-'}</td>
                    <td>
                      <button
                        className="btn-view"
                        onClick={() => setSelectedLog(log)}
                      >
                        View Details
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="audit-logs-pagination">
            <div className="pagination-info">
              Showing {((page - 1) * pageSize) + 1} to {Math.min(page * pageSize, total)} of {total} logs
            </div>
            <div className="pagination-controls">
              <button
                className="btn-page"
                onClick={() => handlePageChange(page - 1)}
                disabled={page === 1}
              >
                Previous
              </button>
              <span className="page-info">
                Page {page} of {totalPages}
              </span>
              <button
                className="btn-page"
                onClick={() => handlePageChange(page + 1)}
                disabled={page >= totalPages}
              >
                Next
              </button>
            </div>
          </div>
        </>
      )}

      {selectedLog && (
        <div className="modal-overlay" onClick={() => setSelectedLog(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Audit Log Details</h2>
              <button className="btn-close" onClick={() => setSelectedLog(null)}>×</button>
            </div>
            <div className="modal-body">
              <div className="detail-row">
                <label>Date/Time:</label>
                <span>{formatDate(selectedLog.createdAt)}</span>
              </div>
              <div className="detail-row">
                <label>Actor:</label>
                <span>
                  {selectedLog.actor ? selectedLog.actor.email : `User ID: ${selectedLog.actorUserId}`}
                </span>
              </div>
              <div className="detail-row">
                <label>Action:</label>
                <span>{selectedLog.action}</span>
              </div>
              <div className="detail-row">
                <label>Target Type:</label>
                <span>{selectedLog.targetType}</span>
              </div>
              <div className="detail-row">
                <label>Target ID:</label>
                <span>{selectedLog.targetId || '-'}</span>
              </div>
              <div className="detail-row">
                <label>IP Address:</label>
                <span>{selectedLog.ipAddress || '-'}</span>
              </div>
              <div className="detail-row">
                <label>User Agent:</label>
                <span>{selectedLog.userAgent || '-'}</span>
              </div>
              {selectedLog.beforeData && (
                <div className="detail-row">
                  <label>Before Data:</label>
                  <pre className="json-data">{formatJson(selectedLog.beforeData)}</pre>
                </div>
              )}
              {selectedLog.afterData && (
                <div className="detail-row">
                  <label>After Data:</label>
                  <pre className="json-data">{formatJson(selectedLog.afterData)}</pre>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AuditLogs;
